import { Capacitor } from '@capacitor/core';
import {
  ErrorCode,
  Platform,
  PurchasePlugin,
  ProductType,
  store,
} from 'capacitor-plugin-cdv-purchase';

const GOOGLE_PLAY_PRODUCTS = Object.freeze([
  'travelsoul_premium',
  'travelsoul_elite',
]);
const GOOGLE_PLAY_COIN_PRODUCTS = Object.freeze([
  'coins_100',
  'coins_500',
  'coins_1200',
  'coins_3000',
]);
const GOOGLE_PLAY_BASE_PLANS = Object.freeze(['monthly', 'yearly']);
const SUBSCRIPTION_GROUP_PREFIX = 'travelsoul_subscription';
const PREMIUM_PRODUCT_ID = 'travelsoul_premium';
const ELITE_PRODUCT_ID = 'travelsoul_elite';
const PREMIUM_TO_ELITE_REPLACEMENT_MODE = 'IMMEDIATE_AND_CHARGE_PRORATED_PRICE';
const ELITE_TO_PREMIUM_REPLACEMENT_MODE = 'DEFERRED';
const MONTHLY_TO_YEARLY_REPLACEMENT_MODE = 'IMMEDIATE_AND_CHARGE_FULL_PRICE';
const YEARLY_TO_MONTHLY_REPLACEMENT_MODE = 'IMMEDIATE_WITHOUT_PRORATION';
const GOOGLE_PLAY_REPLACEMENT_MODES = Object.freeze([
  PREMIUM_TO_ELITE_REPLACEMENT_MODE,
  ELITE_TO_PREMIUM_REPLACEMENT_MODE,
  MONTHLY_TO_YEARLY_REPLACEMENT_MODE,
  YEARLY_TO_MONTHLY_REPLACEMENT_MODE,
]);
const PURCHASE_TIMEOUT_MS = 5 * 60 * 1000;
const PURCHASE_REFRESH_TIMEOUT_MS = 5 * 1000;

let productsRegistered = false;
let initializationPromise = null;
let purchaseInProgress = false;
let purchaseRefreshQueue = Promise.resolve();

function isAndroidNativeRuntime() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

function requireSupportedProduct(productId) {
  if (!GOOGLE_PLAY_PRODUCTS.includes(productId)) {
    throw new Error('google_play_product_unsupported');
  }
}

function requireSupportedCoinProduct(productId) {
  if (!GOOGLE_PLAY_COIN_PRODUCTS.includes(productId)) {
    throw new Error('google_play_coin_product_unsupported');
  }
}

function requireSupportedBasePlan(basePlanId) {
  if (!GOOGLE_PLAY_BASE_PLANS.includes(basePlanId)) {
    throw new Error('google_play_base_plan_unsupported');
  }
}

function registerProductsOnce() {
  if (productsRegistered) return;
  store.register(GOOGLE_PLAY_PRODUCTS.map((id) => ({
    id,
    type: ProductType.PAID_SUBSCRIPTION,
    platform: Platform.GOOGLE_PLAY,
    // Replacements always provide an explicit oldPurchaseToken. Keeping each
    // product in a distinct plugin group prevents automatic cross-tier reuse
    // of a stale token when the provider says there is no active subscription.
    group: `${SUBSCRIPTION_GROUP_PREFIX}_${id}`,
  })));
  store.register(GOOGLE_PLAY_COIN_PRODUCTS.map((id) => ({
    id,
    type: ProductType.CONSUMABLE,
    platform: Platform.GOOGLE_PLAY,
  })));
  productsRegistered = true;
}

function offerIdFor(productId, basePlanId) {
  return `${productId}@${basePlanId}`;
}

function getBasePlanOffer(productId, basePlanId) {
  const product = store.get(productId, Platform.GOOGLE_PLAY);
  return product?.getOffer(offerIdFor(productId, basePlanId));
}

function publicPricingPhase(phase) {
  return {
    displayPrice: phase.price,
    priceMicros: phase.priceMicros,
    currency: phase.currency || null,
    billingPeriod: phase.billingPeriod || null,
    recurrenceMode: phase.recurrenceMode || null,
  };
}

function publicProduct(product) {
  const basePlans = GOOGLE_PLAY_BASE_PLANS.flatMap((basePlanId) => {
    const offer = product.getOffer(offerIdFor(product.id, basePlanId));
    if (!offer) return [];
    return [{
      id: basePlanId,
      pricingPhases: offer.pricingPhases.map(publicPricingPhase),
    }];
  });

  return {
    id: product.id,
    title: product.title,
    description: product.description,
    basePlans,
  };
}

function transactionIncludesProduct(transaction, productId) {
  return transaction.platform === Platform.GOOGLE_PLAY
    && transaction.products.some((product) => product.id === productId);
}

function approvedPurchase(transaction) {
  if (!transaction.purchaseId || transaction.isPending) return null;
  return { purchaseToken: transaction.purchaseId };
}

function transactionIncludesOwnedProduct(transaction, productId) {
  return transaction.platform === Platform.GOOGLE_PLAY
    && !transaction.isPending
    && ['approved', 'finished'].includes(transaction.state)
    && transaction.products.some((product) => product.id === productId);
}

function transactionTimestamp(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const timestamp = value instanceof Date ? value.getTime() : Date.parse(value || '');
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function latestValidTransaction(transactions, productId) {
  const now = Date.now();
  return transactions
    .filter((transaction) => transactionIncludesOwnedProduct(transaction, productId)
      && transaction.purchaseId
      && (!transaction.expirationDate
        || transactionTimestamp(transaction.expirationDate) > now))
    .sort((left, right) => {
      const leftActivityTimestamp = Math.max(
        transactionTimestamp(left.lastRenewalDate),
        transactionTimestamp(left.purchaseDate),
      );
      const rightActivityTimestamp = Math.max(
        transactionTimestamp(right.lastRenewalDate),
        transactionTimestamp(right.purchaseDate),
      );
      return rightActivityTimestamp - leftActivityTimestamp
        || transactionTimestamp(right.expirationDate)
          - transactionTimestamp(left.expirationDate);
    })[0];
}

async function performGooglePlayPurchaseRefresh() {
  let listener;
  let timeoutId;
  let purchaseUpdateReceived;
  let restoreStarted = false;
  const purchaseUpdatePromise = new Promise((resolve) => {
    purchaseUpdateReceived = resolve;
  });

  try {
    listener = await PurchasePlugin.addListener('setPurchases', (event) => {
      if (restoreStarted) purchaseUpdateReceived(event?.purchases);
    });
    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        purchaseUpdateReceived(null);
        resolve({ error: 'subscription_refresh_timed_out' });
      }, PURCHASE_REFRESH_TIMEOUT_MS);
    });
    restoreStarted = true;
    const restorePromise = (async () => {
      const refreshError = await store.restorePurchases();
      if (refreshError) return { error: 'subscription_refresh_failed' };
      const purchases = await purchaseUpdatePromise;
      return Array.isArray(purchases)
        ? { purchases }
        : { error: 'subscription_refresh_timed_out' };
    })();
    return await Promise.race([restorePromise, timeoutPromise]);
  } catch {
    return { error: 'subscription_refresh_failed' };
  } finally {
    clearTimeout(timeoutId);
    await listener?.remove().catch(() => undefined);
  }
}

function refreshGooglePlayPurchases() {
  const refresh = purchaseRefreshQueue.then(performGooglePlayPurchaseRefresh);
  purchaseRefreshQueue = refresh.then(
    () => undefined,
    () => undefined,
  );
  return refresh;
}

function restorePurchaseCandidate(purchase, now) {
  const purchaseToken = purchase?.purchaseId || purchase?.purchaseToken;
  const productIds = Array.isArray(purchase?.products)
    ? purchase.products.map((product) => product.id)
    : purchase?.productIds;
  const supportedProduct = Array.isArray(productIds)
    && productIds.some((productId) => GOOGLE_PLAY_PRODUCTS.includes(productId));
  const supportedTransactionState = ['approved', 'finished', 'initiated']
    .includes(purchase?.state);
  const nativePurchasedState = purchase?.state === undefined
    && purchase?.getPurchaseState === 1;
  const expirationTimestamp = transactionTimestamp(
    purchase?.expirationDate ?? purchase?.expiryTimeMillis,
  );

  if (!purchaseToken
      || !supportedProduct
      || purchase?.isPending === true
      || (!supportedTransactionState && !nativePurchasedState)
      || (expirationTimestamp && expirationTimestamp <= now)) {
    return null;
  }

  return {
    purchaseToken,
    purchaseTimestamp: transactionTimestamp(purchase.purchaseDate ?? purchase.purchaseTime),
    productId: productIds.find((productId) => GOOGLE_PLAY_PRODUCTS.includes(productId)) || null,
  };
}

function restoreDiagnostics(purchases, candidates, overrides = {}) {
  const productIdsSeen = [...new Set(purchases.flatMap((purchase) => {
    if (Array.isArray(purchase?.products)) {
      return purchase.products.map((product) => product?.id).filter(Boolean);
    }
    return Array.isArray(purchase?.productIds) ? purchase.productIds.filter(Boolean) : [];
  }))];
  const transactionStatesSeen = [...new Set(purchases.map((purchase) => {
    if (typeof purchase?.state === 'string') return purchase.state;
    if (purchase?.getPurchaseState === 1) return 'purchased';
    if (purchase?.getPurchaseState === 2) return 'pending';
    return 'unknown';
  }))];
  return {
    totalFreshPurchaseCount: purchases.length,
    eligibleCandidateCount: candidates.length,
    productIdsSeen,
    transactionStatesSeen,
    purchaseTimestampsPresent: candidates.length > 0
      && candidates.every((candidate) => candidate.purchaseTimestamp > 0),
    timestampsTied: false,
    selectedProductId: null,
    googlePlayVerificationError: null,
    ...overrides,
  };
}

function safeGooglePlayVerificationErrorCode(verification) {
  const code = verification?.google_play_verification_error;
  return typeof code === 'string' && /^[a-z0-9_]{1,128}$/.test(code)
    ? code
    : null;
}

function selectCurrentRestorePurchase(purchases) {
  const now = Date.now();
  const candidatesByToken = new Map();
  purchases.forEach((purchase) => {
    const candidate = restorePurchaseCandidate(purchase, now);
    if (!candidate) return;
    const existing = candidatesByToken.get(candidate.purchaseToken);
    if (!existing || candidate.purchaseTimestamp > existing.purchaseTimestamp) {
      candidatesByToken.set(candidate.purchaseToken, candidate);
    }
  });

  const candidates = [...candidatesByToken.values()];
  const diagnostics = restoreDiagnostics(purchases, candidates);
  if (candidates.length === 0) {
    return { code: 'google_play_restore_no_candidate', diagnostics };
  }
  if (candidates.length === 1) {
    return {
      candidate: candidates[0],
      diagnostics: {
        ...diagnostics,
        selectedProductId: candidates[0].productId,
      },
    };
  }
  if (candidates.some((candidate) => candidate.purchaseTimestamp === 0)) {
    return {
      code: 'google_play_restore_ambiguous',
      diagnostics,
    };
  }

  candidates.sort((left, right) => right.purchaseTimestamp - left.purchaseTimestamp);
  if (candidates[0].purchaseTimestamp === candidates[1].purchaseTimestamp) {
    return {
      code: 'google_play_restore_ambiguous',
      diagnostics: {
        ...diagnostics,
        timestampsTied: true,
      },
    };
  }
  return {
    candidate: candidates[0],
    diagnostics: {
      ...diagnostics,
      selectedProductId: candidates[0].productId,
    },
  };
}

function latestFreshTransactionForProduct(purchases, productId) {
  const candidatesByToken = new Map();
  purchases.forEach((purchase) => {
    const candidate = restorePurchaseCandidate(purchase, Date.now());
    if (!candidate || candidate.productId !== productId) return;
    const existing = candidatesByToken.get(candidate.purchaseToken);
    if (!existing || candidate.purchaseTimestamp > existing.purchaseTimestamp) {
      candidatesByToken.set(candidate.purchaseToken, candidate);
    }
  });

  const candidates = [...candidatesByToken.values()];
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  if (candidates.some((candidate) => candidate.purchaseTimestamp === 0)) {
    return { error: 'ambiguous' };
  }

  candidates.sort((left, right) => right.purchaseTimestamp - left.purchaseTimestamp);
  if (candidates[0].purchaseTimestamp === candidates[1].purchaseTimestamp) {
    return { error: 'ambiguous' };
  }
  return candidates[0];
}

async function subscriptionReplacementData(productId, basePlanId, verifyCurrentSubscription) {
  const crossTierReplacement = productId === ELITE_PRODUCT_ID
    ? {
      ownedProductId: PREMIUM_PRODUCT_ID,
      missingTokenError: 'premium_replacement_token_missing',
      replacementMode: PREMIUM_TO_ELITE_REPLACEMENT_MODE,
    }
    : {
      ownedProductId: ELITE_PRODUCT_ID,
      missingTokenError: 'elite_replacement_token_missing',
      replacementMode: ELITE_TO_PREMIUM_REPLACEMENT_MODE,
    };

  const refresh = await refreshGooglePlayPurchases();
  if (refresh.error) return refresh;

  const freshSourceTransaction = latestFreshTransactionForProduct(
    refresh.purchases,
    crossTierReplacement.ownedProductId,
  );
  const currentSubscriptionOwned = store.owned({
    id: crossTierReplacement.ownedProductId,
    platform: Platform.GOOGLE_PLAY,
  });
  // An active Elite must take precedence over a stale locally-owned Premium
  // record when starting the Elite -> Premium deferred downgrade.
  const preferCrossTierReplacement = productId === PREMIUM_PRODUCT_ID
    && (Boolean(freshSourceTransaction) || currentSubscriptionOwned);
  const sameProductOwned = store.owned({
    id: productId,
    platform: Platform.GOOGLE_PLAY,
  });
  if (sameProductOwned && !preferCrossTierReplacement) {
    const currentTransaction = latestValidTransaction(
      store.localTransactions,
      productId,
    );
    if (!currentTransaction?.purchaseId) {
      return { error: 'same_product_replacement_token_missing' };
    }
    if (typeof verifyCurrentSubscription !== 'function') {
      return { error: 'same_product_replacement_verification_unavailable' };
    }
    let verifiedSubscription;
    try {
      const verification = await verifyCurrentSubscription(currentTransaction.purchaseId);
      verifiedSubscription = verification?.google_play_subscription;
    } catch {
      return { error: 'same_product_replacement_verification_failed' };
    }
    if (!verifiedSubscription || typeof verifiedSubscription !== 'object') {
      return { error: 'same_product_replacement_verification_failed' };
    }
    if (verifiedSubscription.product_id !== productId) {
      return { error: 'same_product_replacement_product_mismatch' };
    }
    const currentBasePlanId = verifiedSubscription.base_plan_id;
    if (!currentBasePlanId) {
      return { error: 'same_product_replacement_base_plan_missing' };
    }
    if (!GOOGLE_PLAY_BASE_PLANS.includes(currentBasePlanId)) {
      return { error: 'same_product_replacement_base_plan_unsupported' };
    }
    if (![
      'SUBSCRIPTION_STATE_ACTIVE',
      'SUBSCRIPTION_STATE_IN_GRACE_PERIOD',
      'SUBSCRIPTION_STATE_CANCELED',
    ].includes(verifiedSubscription.provider_state)) {
      return { error: 'same_product_replacement_state_invalid' };
    }
    const currentExpiry = transactionTimestamp(verifiedSubscription.period_expires_at);
    if (!currentExpiry || currentExpiry <= Date.now()) {
      return { error: 'same_product_replacement_expired' };
    }
    if (currentBasePlanId === basePlanId) {
      return { error: 'same_product_base_plan_already_owned' };
    }

    const replacementMode = currentBasePlanId === 'monthly' && basePlanId === 'yearly'
      ? MONTHLY_TO_YEARLY_REPLACEMENT_MODE
      : YEARLY_TO_MONTHLY_REPLACEMENT_MODE;
    return {
      additionalData: {
        googlePlay: {
          oldPurchaseToken: currentTransaction.purchaseId,
          replacementMode,
        },
      },
    };
  }

  if (freshSourceTransaction?.error) {
    return { error: crossTierReplacement.missingTokenError };
  }
  if (!freshSourceTransaction) {
    if (!currentSubscriptionOwned) return { additionalData: undefined };
    return { error: crossTierReplacement.missingTokenError };
  }
  const currentSubscriptionToken = freshSourceTransaction.purchaseToken;

  if (productId === ELITE_PRODUCT_ID) {
    if (typeof verifyCurrentSubscription !== 'function') {
      return { error: 'premium_replacement_verification_unavailable' };
    }
    let verification;
    try {
      verification = await verifyCurrentSubscription(
        currentSubscriptionToken,
      );
    } catch {
      return { error: 'premium_replacement_verification_failed' };
    }

    if (verification?.google_play_verification_error
        === 'google_play_purchase_not_entitled') {
      return { additionalData: undefined };
    }
    if (verification?.google_play_verification_error) {
      return { error: 'premium_replacement_verification_failed' };
    }

    const verifiedSubscription = verification?.google_play_subscription;
    if (!verifiedSubscription || typeof verifiedSubscription !== 'object'
        || verifiedSubscription.product_id !== PREMIUM_PRODUCT_ID) {
      return { error: 'premium_replacement_verification_failed' };
    }
    const entitledState = [
      'SUBSCRIPTION_STATE_ACTIVE',
      'SUBSCRIPTION_STATE_IN_GRACE_PERIOD',
      'SUBSCRIPTION_STATE_CANCELED',
    ].includes(verifiedSubscription.provider_state);
    const knownProviderState = [
      'SUBSCRIPTION_STATE_PENDING',
      'SUBSCRIPTION_STATE_ACTIVE',
      'SUBSCRIPTION_STATE_PAUSED',
      'SUBSCRIPTION_STATE_IN_GRACE_PERIOD',
      'SUBSCRIPTION_STATE_ON_HOLD',
      'SUBSCRIPTION_STATE_CANCELED',
      'SUBSCRIPTION_STATE_EXPIRED',
      'SUBSCRIPTION_STATE_REVOKED',
    ].includes(verifiedSubscription.provider_state);
    const verifiedExpiry = transactionTimestamp(verifiedSubscription.period_expires_at);
    if (!knownProviderState || !verifiedExpiry) {
      return { error: 'premium_replacement_verification_invalid' };
    }
    if (!entitledState || verifiedExpiry <= Date.now()) {
      return { additionalData: undefined };
    }
  } else {
    if (typeof verifyCurrentSubscription !== 'function') {
      return { error: 'elite_replacement_verification_unavailable' };
    }
    let verification;
    try {
      verification = await verifyCurrentSubscription(
        currentSubscriptionToken,
      );
    } catch {
      return { error: 'elite_replacement_verification_failed' };
    }

    if (verification?.google_play_verification_error) {
      return { error: 'elite_replacement_verification_failed' };
    }

    const verifiedSubscription = verification?.google_play_subscription;
    if (!verifiedSubscription || typeof verifiedSubscription !== 'object') {
      return { error: 'elite_replacement_verification_invalid' };
    }
    if (verifiedSubscription.product_id !== ELITE_PRODUCT_ID) {
      return { error: 'elite_replacement_verification_product_mismatch' };
    }
    const entitledState = [
      'SUBSCRIPTION_STATE_ACTIVE',
      'SUBSCRIPTION_STATE_IN_GRACE_PERIOD',
      'SUBSCRIPTION_STATE_CANCELED',
    ].includes(verifiedSubscription.provider_state);
    const knownProviderState = [
      'SUBSCRIPTION_STATE_PENDING',
      'SUBSCRIPTION_STATE_ACTIVE',
      'SUBSCRIPTION_STATE_PAUSED',
      'SUBSCRIPTION_STATE_IN_GRACE_PERIOD',
      'SUBSCRIPTION_STATE_ON_HOLD',
      'SUBSCRIPTION_STATE_CANCELED',
      'SUBSCRIPTION_STATE_EXPIRED',
      'SUBSCRIPTION_STATE_REVOKED',
    ].includes(verifiedSubscription.provider_state);
    const verifiedExpiry = transactionTimestamp(verifiedSubscription.period_expires_at);
    if (!knownProviderState || !entitledState || !verifiedExpiry
        || verifiedExpiry <= Date.now()) {
      return { error: 'elite_replacement_verification_invalid' };
    }
  }

  return {
      additionalData: {
        googlePlay: {
        oldPurchaseToken: currentSubscriptionToken,
        replacementMode: crossTierReplacement.replacementMode,
      },
    },
  };
}

function hasValidSubscriptionReplacementData(replacement) {
  const googlePlay = replacement?.additionalData?.googlePlay;
  return typeof googlePlay?.oldPurchaseToken === 'string'
    && googlePlay.oldPurchaseToken.length > 0
    && GOOGLE_PLAY_REPLACEMENT_MODES.includes(googlePlay.replacementMode);
}

export async function isGooglePlayBillingAvailable() {
  if (!isAndroidNativeRuntime()) return false;
  try {
    await initializeGooglePlayBilling();
    const adapter = store.getAdapter(Platform.GOOGLE_PLAY);
    return Boolean(adapter?.isSupported && adapter.ready);
  } catch {
    return false;
  }
}

export async function initializeGooglePlayBilling() {
  if (!isAndroidNativeRuntime()) return false;
  if (initializationPromise) return initializationPromise;

  registerProductsOnce();
  initializationPromise = store.initialize([Platform.GOOGLE_PLAY]).then((errors) => {
    if (errors.length > 0) throw new Error('google_play_billing_initialization_failed');
    const adapter = store.getAdapter(Platform.GOOGLE_PLAY);
    if (!adapter?.isSupported || !adapter.ready) {
      throw new Error('google_play_billing_unavailable');
    }
    return true;
  });

  return initializationPromise;
}

export async function getGooglePlayProducts() {
  if (!isAndroidNativeRuntime()) return [];
  await initializeGooglePlayBilling();
  await store.update();

  return GOOGLE_PLAY_PRODUCTS.flatMap((productId) => {
    const product = store.get(productId, Platform.GOOGLE_PLAY);
    return product ? [publicProduct(product)] : [];
  });
}

export async function purchaseGooglePlaySubscription(
  productId,
  basePlanId,
  verifyCurrentSubscription,
) {
  if (!isAndroidNativeRuntime()) return { status: 'unavailable' };
  requireSupportedProduct(productId);
  requireSupportedBasePlan(basePlanId);
  if (purchaseInProgress) return { status: 'failed', code: 'purchase_in_progress' };

  await initializeGooglePlayBilling();
  const offer = getBasePlanOffer(productId, basePlanId);
  if (!offer) return { status: 'failed', code: 'offer_unavailable' };
  const replacement = await subscriptionReplacementData(
    productId,
    basePlanId,
    verifyCurrentSubscription,
  );
  if (replacement.error) {
    return {
      status: 'failed',
      code: replacement.error,
    };
  }
  const hasReplacement = hasValidSubscriptionReplacementData(replacement);
  if (replacement.additionalData !== undefined && !hasReplacement) {
    return { status: 'failed', code: 'subscription_replacement_invalid' };
  }
  if (!hasReplacement && !offer.canPurchase) {
    return { status: 'failed', code: 'offer_unavailable' };
  }

  purchaseInProgress = true;
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId;

    const cleanup = () => {
      clearTimeout(timeoutId);
      store.off(onApproved);
      store.off(onPending);
      store.off(onError);
      purchaseInProgress = false;
    };
    const settle = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };
    const onApproved = (transaction) => {
      if (!transactionIncludesProduct(transaction, productId)) return;
      const result = approvedPurchase(transaction);
      settle(result || { status: 'failed', code: 'purchase_token_missing' });
    };
    const onPending = (transaction) => {
      if (transactionIncludesProduct(transaction, productId)) {
        settle({ status: 'pending' });
      }
    };
    const onError = (error) => {
      if (error.productId && error.productId !== productId) return;
      settle(error.code === ErrorCode.PAYMENT_CANCELLED
        ? { status: 'cancelled' }
        : { status: 'failed', code: 'purchase_failed' });
    };

    store.when().approved(onApproved).pending(onPending);
    store.error(onError);
    timeoutId = setTimeout(
      () => settle({ status: 'failed', code: 'purchase_timed_out' }),
      PURCHASE_TIMEOUT_MS,
    );

    offer.order(replacement.additionalData).then((error) => {
      if (!error) return;
      onError(error);
    }).catch(() => settle({ status: 'failed', code: 'purchase_failed' }));
  });
}

// Consumable coin packs: a single Buy offer per product, no replacement data,
// and the product is consumed only after the backend has credited the coins —
// this keeps the local transaction alive for a retry if verification fails.
//
// Because of that, a coin purchase whose backend verification previously
// failed (e.g. the 16.10.2026 apply_coin_purchase_credit ambiguous-column
// bug) leaves an approved-but-unfinished purchase for that exact product on
// Google's side. Depending on what the SDK already has cached locally, this
// surfaces two different ways:
//   - offer.canPurchase is false before we even try (local cache already
//     knows about the stuck purchase) -> "offer_unavailable".
//   - offer.canPurchase is true (local cache is stale/empty), so we call
//     offer.order() anyway, and Google's server rejects it directly with
//     BillingResponseCode 7 / ITEM_ALREADY_OWNED.
// Both cases need the same recovery: find the stuck purchase and replay
// verification + finish() (which consumes it) instead of starting a new
// purchase. Since the local cache may not have the stuck transaction yet
// (that's exactly why ITEM_ALREADY_OWNED happened), force a restore/refresh
// from Google first so store.localTransactions actually has it.
async function recoverStuckCoinTransaction(productId, verifyCoinPurchase) {
  await refreshGooglePlayPurchases();
  const stuckTransaction = latestValidTransaction(store.localTransactions, productId);
  if (!stuckTransaction?.purchaseId) return null;
  if (typeof verifyCoinPurchase !== 'function') return null;

  let verification;
  try {
    verification = await verifyCoinPurchase(productId, stuckTransaction.purchaseId);
  } catch {
    return { status: 'failed', code: 'coin_purchase_verification_failed' };
  }
  if (!verification?.verified) {
    return {
      status: 'failed',
      code: 'coin_purchase_verification_rejected',
      backendError: verification?.error,
    };
  }

  try {
    await stuckTransaction.finish();
  } catch {
    // The backend already credited the coins; a failed local finish just
    // means the transaction may be re-delivered and re-verified harmlessly
    // (verification is idempotent on purchase_token_fingerprint).
  }

  return { status: 'verified', coins: verification.coins };
}

export async function purchaseGooglePlayCoins(productId, verifyCoinPurchase) {
  if (!isAndroidNativeRuntime()) return { status: 'unavailable' };
  requireSupportedCoinProduct(productId);
  if (purchaseInProgress) return { status: 'failed', code: 'purchase_in_progress' };

  await initializeGooglePlayBilling();
  const product = store.get(productId, Platform.GOOGLE_PLAY);
  const offer = product?.getOffer();
  if (!offer || !offer.canPurchase) {
    const recovered = await recoverStuckCoinTransaction(productId, verifyCoinPurchase);
    if (recovered) return recovered;
    return { status: 'failed', code: 'offer_unavailable' };
  }

  purchaseInProgress = true;
  const purchaseResult = await new Promise((resolve) => {
    let settled = false;
    let timeoutId;

    const cleanup = () => {
      clearTimeout(timeoutId);
      store.off(onApproved);
      store.off(onPending);
      store.off(onError);
      purchaseInProgress = false;
    };
    const settle = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };
    const onApproved = (transaction) => {
      if (!transactionIncludesProduct(transaction, productId)) return;
      const approved = approvedPurchase(transaction);
      settle(approved
        ? { status: 'approved', transaction }
        : { status: 'failed', code: 'purchase_token_missing' });
    };
    const onPending = (transaction) => {
      if (transactionIncludesProduct(transaction, productId)) {
        settle({ status: 'pending' });
      }
    };
    const onError = (error) => {
      if (error.productId && error.productId !== productId) return;
      // TEMP DEBUG (remove after diagnosing coin purchase failure): surface
      // the real Billing Library error code/message instead of a bare
      // 'purchase_failed', since that has repeatedly hidden the actual cause.
      settle(error.code === ErrorCode.PAYMENT_CANCELLED
        ? { status: 'cancelled' }
        : {
          status: 'failed',
          code: 'purchase_failed',
          billingErrorCode: error.code,
          billingErrorMessage: error.message,
        });
    };

    store.when().approved(onApproved).pending(onPending);
    store.error(onError);
    timeoutId = setTimeout(
      () => settle({ status: 'failed', code: 'purchase_timed_out' }),
      PURCHASE_TIMEOUT_MS,
    );

    offer.order().then((error) => {
      if (!error) return;
      onError(error);
    }).catch((caughtError) => settle({
      status: 'failed',
      code: 'purchase_failed',
      billingErrorMessage: caughtError?.message || String(caughtError),
    }));
  });

  if (purchaseResult.status !== 'approved') {
    // ITEM_ALREADY_OWNED (Billing code 7): Google's server considers this
    // product already owned by a purchase our local SDK cache didn't know
    // about (offer.canPurchase was true, so the earlier offer_unavailable
    // recovery never ran). Retry the same recovery path now that we know
    // for certain there's a stuck purchase to find.
    if (purchaseResult.status === 'failed' && String(purchaseResult.billingErrorCode) === '7') {
      const recovered = await recoverStuckCoinTransaction(productId, verifyCoinPurchase);
      if (recovered) return recovered;
    }
    return purchaseResult;
  }

  const { transaction } = purchaseResult;
  if (typeof verifyCoinPurchase !== 'function') {
    return { status: 'failed', code: 'coin_purchase_verification_unavailable' };
  }
  let verification;
  try {
    verification = await verifyCoinPurchase(productId, transaction.purchaseId);
  } catch {
    return { status: 'failed', code: 'coin_purchase_verification_failed' };
  }
  if (!verification?.verified) {
    return {
      status: 'failed',
      code: 'coin_purchase_verification_rejected',
      backendError: verification?.error,
    };
  }

  try {
    await transaction.finish();
  } catch {
    // The backend already credited the coins; a failed local finish just
    // means the transaction may be re-delivered and re-verified harmlessly
    // (verification is idempotent on purchase_token_fingerprint).
  }

  return { status: 'verified', coins: verification.coins };
}

export async function restoreGooglePlayPurchases(verifyPurchaseToken) {
  if (!isAndroidNativeRuntime()) {
    return {
      status: 'failed',
      code: 'google_play_restore_unavailable',
      diagnostics: restoreDiagnostics([], []),
    };
  }
  await initializeGooglePlayBilling();

  const refresh = await refreshGooglePlayPurchases();
  if (refresh.error) {
    return {
      status: 'failed',
      code: refresh.error,
      diagnostics: restoreDiagnostics([], []),
    };
  }
  const selection = selectCurrentRestorePurchase(refresh.purchases);
  if (selection.code) {
    return {
      status: selection.code === 'google_play_restore_no_candidate' ? 'none' : 'failed',
      code: selection.code,
      diagnostics: selection.diagnostics,
    };
  }
  if (typeof verifyPurchaseToken !== 'function') {
    return {
      status: 'failed',
      code: 'google_play_restore_verification_unavailable',
      diagnostics: selection.diagnostics,
    };
  }
  try {
    const verification = await verifyPurchaseToken(selection.candidate.purchaseToken);
    if (!verification?.google_play_subscription) {
      return {
        status: 'failed',
        code: 'google_play_restore_verification_rejected',
        diagnostics: {
          ...selection.diagnostics,
          googlePlayVerificationError: safeGooglePlayVerificationErrorCode(
            verification,
          ),
        },
      };
    }
  } catch {
    return {
      status: 'failed',
      code: 'google_play_restore_verification_rejected',
      diagnostics: selection.diagnostics,
    };
  }
  return {
    status: 'verified',
    code: 'google_play_restore_verified',
    diagnostics: selection.diagnostics,
  };
}
